"use client";

import Link from "next/link";
import { useEffect } from "react";
import { Button } from "@/modules/common/components/button";
import { logger } from "@/lib/util/logger";

/**
 * Backstop for anything that escapes a route under /[countryCode] — it sits
 * above both (main) and (checkout), so one file covers the whole storefront.
 *
 * It renders inside the ROOT layout, outside CustomerProvider, so it must not
 * pull in <Header /> or anything else that needs those contexts: the tree
 * below is exactly what just failed.
 */
export default function CountryCodeError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    logger.error("Unhandled route error", { digest: error.digest }, "ERROR");
  }, [error]);

  return (
    <main className="flex min-h-screen flex-1 items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-[0_10px_40px_rgba(0,0,0,0.15)] md:p-12">
          <h1 className="font-achiko text-green-main mb-4 text-3xl font-bold md:text-4xl">
            Lo sentimos, algo salió mal
          </h1>
          <p className="font-inter mx-auto mb-8 max-w-md text-gray-600">
            No pudimos completar la operación. Vuelve a intentarlo; si el
            problema continúa, escríbenos y lo revisamos.
          </p>

          {/* Only the digest. error.message in production is Next's redaction
              paragraph ("An error occurred in the Server Components render..."),
              which is exactly the unfriendly text we are here to stop showing. */}
          {error.digest && (
            <p className="mb-6 text-xs text-gray-400">
              Código de referencia: {error.digest}
            </p>
          )}

          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            <Button type="button" onClick={reset}>
              Reintentar
            </Button>
            <Button type="button" variant="outline" asChild>
              <Link href="/">Volver a la tienda</Link>
            </Button>
          </div>
        </div>
      </div>
    </main>
  );
}
